import React, { useState } from "react";
import { useOrganization } from "../../context/OrganizationContext";
import styles from "./BillingWarningBanner.module.css";

// Proactive warning thresholds, expressed as a fraction of the school's
// freePassLimit remaining — so admins get a heads-up before hitting the 422
// lockout on POST /passes instead of only reacting to it. "Urgent" swaps in
// louder styling once the remaining share drops further.
const WARNING_REMAINING_RATIO = 0.1; // warn at <=10% of the free-pass limit left
const URGENT_REMAINING_RATIO = 0.02; // escalate styling at <=2% left

const BillingWarningBanner = (): React.JSX.Element | null => {
  const { billingStatus, currentOrg } = useOrganization();
  const [dismissed, setDismissed] = useState(false);

  const { isPaid, remaining, freePassLimit } = billingStatus || {};

  const remainingRatio =
    typeof remaining === "number" &&
    typeof freePassLimit === "number" &&
    freePassLimit > 0
      ? remaining / freePassLimit
      : null;

  // Only show a school that isn't paid, still has free passes left (a fully
  // exhausted school gets BillingRequired's lockout screen instead), and is
  // within the warning threshold of its limit.
  const shouldWarn =
    !isPaid &&
    remainingRatio !== null &&
    typeof remaining === "number" &&
    remaining > 0 &&
    remainingRatio <= WARNING_REMAINING_RATIO;

  if (!shouldWarn || dismissed) {
    return null;
  }

  const isUrgent = remainingRatio <= URGENT_REMAINING_RATIO;

  return (
    <div
      className={`${styles["billing-warning-banner"]} ${isUrgent ? styles["urgent"] : ""}`}
    >
      <div className={styles["banner-content"]}>
        <span className={styles["warning-icon"]}>{isUrgent ? "⚠️" : "⏰"}</span>

        <div className={styles["warning-text"]}>
          <strong>{currentOrg?.name}</strong>
          {" - "}
          Approaching free pass limit: {remaining} of {freePassLimit} free
          passes remaining.
        </div>

        <div className={styles["banner-actions"]}>
          <button
            className={styles["dismiss-button"]}
            onClick={() => setDismissed(true)}
            aria-label="Dismiss warning"
          >
            ✕
          </button>
        </div>
      </div>

      {isUrgent && (
        <div
          className={styles["urgency-bar"]}
          style={{
            width: `${remainingRatio * 100}%`,
          }}
        />
      )}
    </div>
  );
};

export default BillingWarningBanner;
